"use client";

import Link from "next/link";
import { useEffect, useRef, type CSSProperties, type MouseEvent } from "react";
import { wixSized, wixSrcSet } from "../../../lib/media";
import type { RailTile } from "../../../lib/brand-shop";

/**
 * The lineup rail under the brand wordmark. The motion shell (adapted from
 * the 21st.dev "scroll morph" hero, where scattered tiles resolve into order)
 * runs once on load: each tile drops from a scattered offset onto a shelf
 * line with a 60 ms stagger. On desktop the whole rail then drifts sideways
 * with the page scroll; on phones it is a plain horizontal snap scroller.
 * Everything rests under prefers-reduced-motion and the final layout is
 * reserved from the first paint, so nothing shifts.
 *
 * A tile that carries `line` filters the grid below in place (custom event
 * picked up by BrandShop) and falls back to a real link without JS.
 */

const RAIL_WIDTHS = [160, 224, 320, 448];

/** Deterministic "scatter" offsets per tile, so SSR and client agree. */
function scatter(i: number): CSSProperties {
  const r = (k: number) => (((i + 1) * k) % 97) / 97;
  const dx = Math.round((r(37) - 0.5) * 84);
  const dy = -Math.round(28 + r(53) * 44);
  const rot = ((r(29) - 0.5) * 10).toFixed(1);
  return { "--i": i, "--dx": `${dx}px`, "--dy": `${dy}px`, "--rot": `${rot}deg` } as CSSProperties;
}

export default function BrandRail({ tiles, label }: { tiles: RailTile[]; label: string }) {
  const trackRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 768px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let raf = 0;
    const apply = () => {
      raf = 0;
      if (!fine.matches || reduce.matches) {
        el.style.transform = "";
        return;
      }
      const y = Math.min(window.scrollY, 720);
      el.style.transform = `translate3d(${(-y * 0.22).toFixed(1)}px,0,0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    fine.addEventListener("change", onScroll);
    reduce.addEventListener("change", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      fine.removeEventListener("change", onScroll);
      reduce.removeEventListener("change", onScroll);
      if (raf) cancelAnimationFrame(raf);
      el.style.transform = "";
    };
  }, []);

  function onLineClick(e: MouseEvent<HTMLAnchorElement>, line: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const shop = document.getElementById("shop");
    if (!shop) return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("brand-shop:set", { detail: { line } }));
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    shop.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }

  if (tiles.length === 0) return null;

  return (
    <div className="brand-rail" role="region" aria-label={`${label} lineup`}>
      <ul ref={trackRef} className="brand-rail-track">
        {tiles.map((t, i) => (
          <li key={t.href} className="rail-tile" style={scatter(i)}>
            <Link
              href={t.href}
              className="rail-link"
              onClick={t.line ? (e) => onLineClick(e, t.line!) : undefined}
            >
              <span className="rail-img">
                {t.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={wixSized(t.image, 448, 448)}
                    srcSet={wixSrcSet(t.image, RAIL_WIDTHS, 1)}
                    sizes="(min-width: 1280px) 224px, (min-width: 768px) 200px, 156px"
                    alt={t.alt}
                    width={448}
                    height={448}
                    loading={i < 4 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : undefined}
                    decoding="async"
                    className="brand-cutout"
                  />
                )}
              </span>
              <span className="rail-shadow" aria-hidden="true" />
              <span className="rail-label">{t.label}</span>
              <span className="rail-sub">{t.sub}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
